import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, ListGroup, InputGroup } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { Search } from 'lucide-react';
import styles from '../styles/components/CreateChatForm.module.css';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch} from '@fortawesome/free-solid-svg-icons';

export default function CreateChatForm({ show, handleClose, onCreateChat }) {
  const [title, setTitle] = useState('');
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const { getAccessTokenSilently, user, isAuthenticated, isLoading } = useAuth0();

  const removeSpecialCharacters = (str) => {
    return str.replace(/[-|]/g, '');
  };

  useEffect(() => {
    const fetchFriends = async () => {
      if (show && isAuthenticated && !isLoading) {
        setLoading(true);
        setError(null);
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_API_AUDIENCE,
            },
          });
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/friends/getfriends`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            const cleanUserSub = removeSpecialCharacters(user.sub);
            const friendsData = data.map(friend => {
              const cleanRequesterId = removeSpecialCharacters(friend.requesterId);
              const cleanRecipientId = removeSpecialCharacters(friend.recipientId);
              let friendUserId;
              if (cleanRequesterId === cleanUserSub) {
                friendUserId = friend.recipientId;
              } else if (cleanRecipientId === cleanUserSub) {
                friendUserId = friend.requesterId;
              } else {
                console.log(`Diagnostic: Cleaned user.sub (${cleanUserSub}) doesn't match cleaned requesterId (${cleanRequesterId}) or cleaned recipientId (${cleanRecipientId}) for friend with id ${friend.id}`);
                friendUserId = null;
              }
              return {
                id: friend.id,
                userId: friendUserId,
                name: friendUserId // Using userId as name for simplicity. You may want to fetch actual names if available.
              };
            }).filter(friend => friend.userId !== null);


            // Fetch user handles
            const userIds = friendsData.map(friend => friend.userId);
            const token = await getAccessTokenSilently({
              authorizationParams: {
                audience: process.env.REACT_APP_API_AUDIENCE,
              },
            });
            const handleResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/Users/GetUserHandles`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(userIds),
            });
            if (handleResponse.ok) {
              const handles = await handleResponse.json();
              friendsData.forEach(friend => {
                const handle = handles.find(h => h.userId === friend.userId);
                if (handle) {
                  friend.name = handle.userHandle;
                }
              });
            }


            friendsData.sort((a, b) => a.name.localeCompare(b.name));
            setFriends(friendsData);
          } else {
            throw new Error('Failed to fetch friends');
          }
        } catch (error) {
          console.error('Error fetching friends:', error);
          setError('Failed to load friends. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchFriends();
  }, [getAccessTokenSilently, show, user, isAuthenticated, isLoading]);

  const filteredFriends = useMemo(() => {
    return friends.filter(friend =>
      friend.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [friends, searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);
    if (title && selectedFriends.length > 0 && isAuthenticated && !isLoading) {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_API_AUDIENCE,
          },
        });
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Chats/CreateChat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title,
            participants: [user.sub, ...selectedFriends.map(friendId => {
              const friend = friends.find(f => f.id === friendId);
              return friend ? friend.userId : friendId;
            })],
          }),
        });

        if (response.ok) {
          const newTopic = await response.json();
          onCreateChat(newTopic);
          handleClose();
          setTitle('');
          setSelectedFriends([]);
          setShowValidation(false);
        } else {
          throw new Error('Failed to create chat');
        }
      } catch (error) {
        console.error('Error creating chat:', error);
        setError('Failed to create chat. Please try again.');
      }
    }
  };

  const toggleFriendSelection = (e, friendId) => {
    e.preventDefault();
    setSelectedFriends(prev => 
      prev.includes(friendId) 
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };

  if (isLoading || !isAuthenticated || !show) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h5 className={styles.modalTitle}>Create New Chat</h5>
          <button type="button" className={styles.closeButton} onClick={handleClose}>&times;</button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.container}>
            {error && <div className={styles.error}>{error}</div>}
            <Form onSubmit={handleSubmit}>
              <div className={styles.sectionCard}>
                <Form.Group className={styles.formGroup}>
                  <Form.Label className={styles.formLabel}>
                    Chat Title <span className={styles.requiredMark}>Required</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter chat title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    aria-invalid={showValidation && !title}
                    className={`${styles.input} ${styles.centerAligned} ${
                      showValidation && !title ? styles.inputError : ''
                    }`}
                  />
                  {showValidation && !title && (
                    <div className={styles.validationMessage}>Please enter a chat title.</div>
                  )}
                </Form.Group>
              </div>
              
              <div className={styles.friendsContainer}>
                <div className={`${styles.friendsList} ${styles.sectionCard}`}>
                  <Form.Group>
                    <Link to="/user-search" className="btn btn-outline-primary btn-sm me-2 mb-2" onClick={handleClose}>
                      <FontAwesomeIcon icon={faSearch} className="me-2"/>
                      Find a Friend
                    </Link>
                    <div className={styles.friendsHeader}>
                      <Form.Label className={styles.formLabel}>
                        Select Friends <span className={styles.requiredMark}>Required</span>
                      </Form.Label>
                      {showValidation && selectedFriends.length === 0 && (
                        <div className={styles.validationMessage}>
                          Choose at least one friend to start a chat.
                        </div>
                      )}
                      <InputGroup className={styles.searchInput}>
                        <InputGroup.Text className={styles.searchIcon}>
                          <Search size={18} />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search friends..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className={styles.input}
                        />
                      </InputGroup>
                    </div>
                    {loading ? (
                      <div className={styles.loading}>
                        <div className={styles.spinner}></div>
                        <span className={styles.srOnly}>Loading friends...</span>
                      </div>
                    ) : filteredFriends.length > 0 ? (
                      <ListGroup className={styles.scrollable}>
                        {filteredFriends.map((friend) => (
                          <ListGroup.Item
                            key={friend.id}
                            action
                            onClick={(e) => toggleFriendSelection(e, friend.id)}
                            active={selectedFriends.includes(friend.id)}
                            className={`${styles.friendItem} ${selectedFriends.includes(friend.id) ? styles.activeFriend : ''}`}
                          >
                            {friend.name}
                            {selectedFriends.includes(friend.id) && (
                              <span className={styles.selectedBadge}>Selected</span>
                            )}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <p>No friends found. Try a different search term or add some friends to start a chat!</p>
                    )}
                  </Form.Group>
                </div>

                <div className={`${styles.selectedFriends} ${styles.sectionCard}`}>
                  <Form.Group>
                    <Form.Label className={styles.formLabel}>Selected Friends</Form.Label>
                    <ListGroup className={styles.scrollable}>
                      {selectedFriends.length === 0 ? (
                        <ListGroup.Item className={styles.friendItem}>No friends selected</ListGroup.Item>
                      ) : (
                        selectedFriends.map(friendId => {
                          const friend = friends.find(f => f.id === friendId);
                          return (
                            <ListGroup.Item 
                              key={friendId} 
                              className={styles.selectedFriendItem}
                            >
                              {friend ? friend.name : 'Unknown'}
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={(e) => toggleFriendSelection(e, friendId)}
                                className={styles.removeButton}
                              >
                                Remove
                              </Button>
                            </ListGroup.Item>
                          );
                        })
                      )}
                    </ListGroup>
                  </Form.Group>
                </div>
              </div>

              <Button 
                variant="primary" 
                type="submit" 
                disabled={!title || selectedFriends.length === 0 || loading}
                className={styles.submitButton}
              >
                Create Chat
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
